import React, { useEffect } from 'react';
import {
    BrowserRouter as Router, Routes, Route, Outlet
} from "react-router-dom";
import { Paper, Box, Container } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { lightTheme, darkTheme } from '@lp/lp-ui'
import Copyright from './components/copyright';
import Logo from './components/logo';
import ProfileHeader from './components/profileHeader';
import Client from './components/Client';
import Profile from './components/Profile';
import './index.css';
import SigninOidc from './components/SigninOidc';
import SilentRenew from './components/SilentReniew';
import { useAppDispatch } from './app/hooks';
import authService from './services/authService';
import { currentUserUnloaded, getCurrentUser, getCurrentUserApplications } from './app/currentUserSlice';
import App from './components/App';
import Redirect from './components/Client/Redirect';
import AccessDeny from './components/AccessDeny';

const Main: React.FC = () => {
    const dispatch = useAppDispatch();

    useEffect(() => {
        authService.getUser()
            .then(user => {
                if (user !== null) {
                    dispatch(getCurrentUser());
                    dispatch(getCurrentUserApplications());
                }
            });

        const userLoaded = () => {
            dispatch(getCurrentUser());
            dispatch(getCurrentUserApplications());
        }
        const userUnloaded = () => {
            dispatch(currentUserUnloaded());
        };

        authService.userManager.events.addUserLoaded(userLoaded);
        authService.userManager.events.addUserUnloaded(userUnloaded);

        return () => {
            authService.userManager.events.removeUserLoaded(userLoaded);
            authService.userManager.events.removeUserUnloaded(userUnloaded);
        };
    }, []);

    return (
        <Router>
            <Routes>
                <Route element={<LoginLayout />}>
                    <Route path="/" element={<App />} />
                    <Route path="/client" element={<Client />} />
                    <Route path="/client/redirect" element={<Redirect />} />
                    <Route path="/signin-oidc" element={<SigninOidc />} />
                </Route>
                <Route element={<ProfileLayout />}>
                    <Route path="/account" element={<Profile />} />
                    <Route path="/access-deny" element={<AccessDeny />} />
                </Route>
                <Route path="/silent-renew" element={<SilentRenew />} />
            </Routes>
        </Router>
    )
}

const theme = createTheme({...lightTheme,
    typography: {
        ...lightTheme.typography,
        button: {
            textTransform: "none"
        },
        body1: {
            fontSize: 13
        },
        subtitle1: {
            fontSize: 13
        }
    },
    components: {
        ...lightTheme.components,
        MuiTextField: {
            ...lightTheme.components!.MuiTextField,
            defaultProps: {
                variant: "outlined"
            }
        },
        MuiLink: {
            defaultProps: {
                underline: "none"
            }
        },
        MuiButton: {
            defaultProps: {
                size: "large"
            }
        },
        MuiInputLabel: {},
        MuiFormLabel: {},
        MuiFormHelperText: {}
    }
});

const LoginLayout = () => (
    <ThemeProvider theme={theme}>
        <Container component="main" maxWidth={false} sx={{
            textAlign: 'center',
            maxWidth: '400px',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
        }}>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}></Box>
            <Box sx={{ marginTop: { xs: 3, md: 0} }}>
                <Logo sx={{ mb: { xs: 0, md: 1.5 } }} />
                <Paper sx={{
                        backgroundColor: { xs: '#fafafa', md: '#ffffff' },
                        boxShadow: { xs: "none", md: 1 }
                    }}
                >
                    <Box
                        sx={{
                            pl: 3, pr: 3, pt: 2, pb: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                        <Outlet />
                    </Box>
                </Paper>
            </Box>
            <Copyright sx={{ mb: 2 }} />
        </Container>
    </ThemeProvider>
);

const ProfileLayout = () => (
    <ThemeProvider theme={lightTheme}>
        <Container component="div" maxWidth="lg" sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
        }}>
            <Box>
                <ProfileHeader sx={{ mb: 1.3, mt: 0.2 }} />
                <Box>
                    <Outlet />
                </Box>
            </Box>
            <Copyright sx={{ m: 2 }} />
        </Container>
    </ThemeProvider>
);

export default Main;
